export enum Brand {
  Vw = 'V',
  Audi = 'A',
  Commercial = `C`,
}

export enum Title {
  Dr = 'Dr',
  Prof = 'Prof',
  Hon = 'Hon',
  Ms = 'Ms',
  Miss = 'Miss',
  Mev = 'Mev.',
  Mej = 'Mej.',
  Mr = 'Mr',
  Mnr = 'Mnr.',
  Mrs = 'Mrs',
  Rev = 'Rev',
  Me = 'Me.',
  Adv = 'Adv'
}

export interface ServicePlan {
  planType: string;
  planStatus: string;
  planStartDate: Date | null;
  planEndDate: Date | null;
  planExpiredMileage: string;
}

export enum ContactMethodType {
  SMS = 'SMS',
  Email = 'Email',
}

export interface VehicleDetails {
  title: Title;
  firstName: string;
  lastName: string;
  mobile: string;
  email: string;
  vin: string;
  registrationNumber: string;
  modelCode: string;
  modelDescription: string;
  modelYear: string;
  brand: Brand;
  plans: ServicePlan[];
}

export interface VehicleDetailsRequest {
  customerId: string;
  // Sent instead of customerId when the customer logs in by VIN with a passport. Without it
  // the lookup went out with the VIN alone and was rejected, so those customers could not log in.
  passportNumber?: string;
  vin: string;
}

export interface CustomerIdentityRequest {
  customerId: string;
  passportNumber: string;
  registrationNumber: string;
}

export interface CustomerIdentity {
  customerId: string;
  vin: string;
}

export interface VehicleServiceHistory {
  serviceDate: Date;
  dealerCode: string;
  dealerName: string;
  serviceDescription: string;
  mileage: number;
}

export interface TCOSRequest {
  vinNumber: string;
  modelCode: string;
  currentMileage: number;
}

export interface TCOS {
  dealerRetailLabourRate: number;
  repairOperations: RepairOperation[];
  parts: Part[];
}

export interface RepairOperationEstimationRequest {
  operationCode: string;
  timeUnits: number;
}

export interface RepairOperationEstimation {
  operationCode: string;
  timeUnits: number;
  price: number;
}

export interface PartEstimationRequest {
  partNumber: string;
  quantity: number;
}

export interface PartEstimation {
  partNumber: string;
  quantity: number;
  price: number;
}

export interface TCOSEstimationRequest {
  dealerCode: string;
  repairOperations: RepairOperationEstimationRequest[];
  parts: PartEstimationRequest[];
}

export interface TCOSEstimation {
  repairOperationsEstimations: RepairOperationEstimation[];
  partsEstimations: PartEstimation[];
  totalOperations: number;
  totalParts: number;
  total: number;
  vat: number;
  estimation: number;
}

export interface CustomerContact {
  title: Title;
  name: string;
  surname: string;
  email: string;
  phoneNumber: string;
  preferredContactMethodType: ContactMethodType;
}

export interface BookingSlotsRequest {
  dealerCode: string;
  startDate: Date;
  endDate: Date;
}

export interface BookingSlots {
  bookingSlots: BookingSlot[];
}

export interface BookingSlot {
  id?: string;
  date: Date;
}

export interface Part {
  partNumber: string;
  quantity: number;
}

export interface RepairOperation {
  operationCode: string;
  timeUnits: number;
}

export interface ServiceType {
  name: string;
}

export interface ServiceRequest {
  serviceType: ServiceType;
  serviceComments?: string;
  mileage: number;
}

export interface EstimationRequest {
  dealerCode: string;
}

export interface Estimation {
  available: boolean;
}

export interface Dealer {
  dealerCode: string;
  brand: string;
  name: string;
  address: string;
  suburb: string;
  city: string;
  postalCode: string;
  country: string;
  latitude: string;
  longitude: string;
}

export interface BookingsRequest {
  customerId: string;
  passportNumber: string;
  vin: string;
}

export interface Booking {
  id: number;
  customerId: string;
  passportNumber: string;
  registrationNumber: string;
  vin: string;
  bookingRef: string;
  dealerName: string;
  dealerCode: string;
  serviceType: string;
  bookingDate: Date;
  additionalNotes: string;
}

export interface CancelBookingRequest {
  referenceNumber: string;
}

export interface CreateBookingRequest {
  dealerCode: string;
  dealerName: string;
  slot: {
    id?: string;
    date: Date;
  };
  customer: {
    customerId?: string;
    passportNumber?: string;
    registrationNumber?: string;
    title: Title;
    firstName: string;
    lastName: string;
    mobile: string;
    email: string;
    preferredContactMethodType: ContactMethodType;
  };
  vehicle: {
    regNo: string;
    vin: string;
    brand: Brand;
    modelCode: string;
    modelDescription: string;
    modelYear: string;
    mileage: number;
  };
  requirements: {
    service: string;
    serviceNotes: string;
  };
  recaptchaToken?: string;
  recaptchaAction?: string;
}

export interface CreateBookingResponse {
  bookingId: string;
}
